import { isAfter, isSameDay } from 'date-fns';
import { FormikErrors } from 'formik';

import {
    ContractPriceListRequestMessage,
    ContractMessage,
    ContractState,
    EffectiveDateMode,
    PriceListActivationMode,
} from '../../../types/ContractMessage';
import { ValidationResponse } from '../../../types/ValidationResponse';
import { ValidationResponseCode } from '../../../types/ValidationResponseCode';
import { CreateContractMessage } from '../../common/api/clients/contracts.api';
import { translate } from '../../common/helpers/translate.helper';
import { ContractFormValues } from '../../contracts/form/contractForm.types';
import { shouldAutoApplyPriceList } from '../ContractForm/PriceListSection/priceListValidation.helpers';

export const getInitialContractFormValues = (
    contract?: ContractMessage,
    isCopy = false,
    customerIds?: string[]
): ContractFormValues => ({
    state: isCopy ? ContractState.DRAFT : contract?.state ?? ContractState.DRAFT,
    customerGroupId: contract?.customerGroupId ?? '',
    name: isCopy ? translate('contracts.form.copyName', { name: contract?.name ?? '' }) : contract?.name ?? '',
    contractReference: contract?.contractReference ?? '',
    description: contract?.description ?? '',
    supplierId: contract?.supplierId ?? '',
    supplierName: contract?.supplier?.name ?? '',
    customerIds: isCopy ? customerIds ?? [] : contract?.customerIds ?? [],
    excludedVesselIds: contract?.excludedVesselIds ?? [],
    minimumVolume: contract?.minimumVolume ? contract.minimumVolume.toString() : '',
    validFrom: isCopy ? null : contract?.validFrom ?? null,
    validUntil: isCopy ? null : contract?.validUntil ?? null,
    evergreen: isCopy ? false : contract?.evergreen ?? false,
    paymentTermReference: contract?.paymentTermReference,
    paymentTermReferenceDays: contract?.paymentTermReferenceDays ? contract.paymentTermReferenceDays.toString() : '',
    gtc: contract?.gtc ?? '',
    priceLists: [],
    effectiveDateMode: contract?.effectiveDateMode ?? EffectiveDateMode.DELIVERY_DATE,
    priceListActivationMode: contract?.priceListActivationMode ?? PriceListActivationMode.AUTO,
});

export const toContractPayload = (values: ContractFormValues): CreateContractMessage => {
    const hasValidUntil = Boolean(values.validUntil);
    const hasValidFrom = Boolean(values.validFrom);
    const isDraft = values.state === ContractState.DRAFT;

    let evergreen = values.evergreen;

    if (isDraft && !hasValidFrom && !hasValidUntil) {
        evergreen = false;
    }

    if (hasValidFrom && !hasValidUntil) {
        evergreen = true;
    }

    return {
        name: values.name,
        state: values.state,
        customerGroupId: values.customerGroupId || undefined,
        contractReference: values.contractReference || undefined,
        description: values.description || undefined,
        supplierId: values.supplierId || undefined,
        customerIds: values.customerIds,
        excludedVesselIds: values.excludedVesselIds,
        minimumVolume: Number(values.minimumVolume) || undefined,
        validFrom: values.validFrom ?? undefined,
        validUntil: values.validUntil ?? undefined,
        evergreen,
        paymentTermReference: values.paymentTermReference,
        paymentTermReferenceDays: Number(values.paymentTermReferenceDays) || undefined,
        gtc: values.gtc || undefined,
        effectiveDateMode: values.effectiveDateMode,
        priceListActivationMode: values.priceLists.length > 0 ? values.priceListActivationMode : undefined,
    };
};

export const toPriceListPayload = (values: ContractFormValues): ContractPriceListRequestMessage[] =>
    values.priceLists.map((pl) => ({
        originalFileId: pl.fileId,
        validFrom: pl.validFrom ?? undefined,
        validUntil: pl.validUntil ?? undefined,
        activeOnApproval:
            values.priceListActivationMode === PriceListActivationMode.AUTO
                ? shouldAutoApplyPriceList(pl)
                : pl.activeOnApproval,
    }));

export const validateContractForm = (values: ContractFormValues): FormikErrors<ContractFormValues> => {
    const errors: FormikErrors<ContractFormValues> = {};

    if (
        values.validFrom &&
        values.validUntil &&
        isAfter(new Date(values.validFrom), new Date(values.validUntil)) &&
        !isSameDay(new Date(values.validFrom), new Date(values.validUntil))
    ) {
        errors.validUntil = translate('contracts.form.validation.validUntilAfterValidFrom');
    }

    return errors;
};

export const mapBackendValidationErrors = (
    response?: ValidationResponse,
    values?: Pick<ContractFormValues, 'validFrom'>
): FormikErrors<ContractFormValues> => {
    const fieldErrors: FormikErrors<ContractFormValues> = {};

    if (!response || response.code !== ValidationResponseCode.VALIDATION_FAILED) {
        return fieldErrors;
    }

    response.errors.forEach((error) => {
        if (error.code === 'INVALID_CONTRACT_VALIDITY' && !values?.validFrom) {
            return;
        }

        const field = error.path.split('.').pop();

        if (error.code === 'PAYMENT_TERM_DAYS_REQUIRED' && field === 'paymentTermReferenceDays') {
            fieldErrors.paymentTermReferenceDays = translate(
                'contracts.form.validation.backend.PAYMENT_TERM_DAYS_REQUIRED'
            );
            return;
        }

        if (error.code === 'PRICE_LIST_EXCEEDS_CONTRACT_VALIDITY' && field === 'validUntil') {
            fieldErrors.validUntil = translate(
                'contracts.form.validation.backend.PRICE_LIST_EXCEEDS_CONTRACT_VALIDITY'
            );
            return;
        }

        if (error.code === 'REQUIRED') {
            switch (field) {
                case 'name':
                    fieldErrors.name = translate('contracts.form.validation.backend.required.name');
                    return;
                case 'customerGroupId':
                    fieldErrors.customerGroupId = translate('contracts.form.validation.customerGroupRequired');
                    return;
                case 'supplierId':
                    fieldErrors.supplierId = translate('contracts.form.validation.backend.required.supplierId');
                    return;
                case 'gtc':
                    fieldErrors.gtc = translate('contracts.form.validation.backend.required.gtc');
                    return;
                case 'paymentTermReference':
                    fieldErrors.paymentTermReference = translate(
                        'contracts.form.validation.backend.required.paymentTermReference'
                    );
                    return;
                case 'validFrom':
                    fieldErrors.validFrom = translate('contracts.form.validation.backend.required.validFrom');
                    return;
                default:
                    return;
            }
        }

        return;
    });

    return fieldErrors;
};

export const getPublishedContractState = (validFrom?: string | null): ContractState => {
    if (validFrom && isSameDay(new Date(validFrom), new Date())) {
        return ContractState.ACTIVE;
    }

    return ContractState.AWARDED;
};
